import { useEffect, useState, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import { ContractorsListTable } from './Components/ContractorsListTable';
import { PropertySelector } from './Components/PropertySelector';
import { AddEditContractorModal } from './ActionModals/AddEditContractorModal';
import { DeleteContractorConfirmationModal } from './ActionModals/DeleteContractorConfirmationModal';

import {
  defaultContractor,
  getContractorInitialValues,
  getValidationSchema,
} from './Helpers/Config';

import { getPropertyList } from '../../Inventory/pages/FacilityInventory/Helpers/Api';

import {
  createContractor,
  getAllContractors,
  updateContractor,
  deleteContractor,
  getContractorCategories,
} from './Helpers/Api';

import { setLoading } from '../../../store/actions';

const Contractors = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [formMode, setFormMode] = useState('add');

  const [selectedFilters, setSelectedFilters] = useState({
    property_id: '',
    building_name: '',
    category: '',
    search: '',
  });

  const validationSchema = useMemo(
    () => getValidationSchema(formMode),
    [formMode]
  );

  const getUserFromToken = () => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  };

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;

    setUser(currentUser);

    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser.role, currentUser.id);
      setProperties(result?.propertyList || []);
    } catch {
      Swal.fire('Error', 'Failed to fetch properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const fetchCategories = useCallback(async () => {
    try {
      const cats = await getContractorCategories();
      setCategories(cats || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const fetchContractors = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const params = {
        propertyId: selectedFilters.property_id || undefined,
        category: selectedFilters.category || undefined,
        search: selectedFilters.search || undefined,
        limit: 50,
        page: 1,
      };

      const response = await getAllContractors(params);
      setContractors(response?.data || []);
    } catch {
      setContractors([]);
      Swal.fire('Error', 'Could not load contractors', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, selectedFilters]);

  /**
   * Prepares payload for create/update - transforms form values
   * to the format expected by the backend (properties as array)
   */
  const preparePayload = (values) => {
    const payload = { ...values };
    if (!payload.company_id) delete payload.company_id;
    if (!payload.description) delete payload.description;
    if (Array.isArray(payload.properties)) {
      payload.properties = payload.properties
        .filter(p => p?.property_id && p.building_name)
        .map(p => ({
          property_id: p.property_id,
          building_name: p.building_name.trim(),
        }));
    }

    return payload;
  };

  const submitContractor = async (values) => {
    const payload = preparePayload(values);

    if (selectedContractor?._id) {
      await updateContractor(selectedContractor._id, payload);
      return selectedContractor._id;
    }

    const response = await createContractor(payload);
    fetchCategories();
    return response?._id || response?.data?._id;
  };

  const formik = useFormik({
    initialValues: defaultContractor,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        await submitContractor(values);
        Swal.fire('Success', 'Contractor saved successfully', 'success');
        setIsModalOpen(false);
        resetForm();
        fetchContractors();
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Failed to save contractor', 'error');
      }
    },
  });

  const handleAddClick = () => {
    setFormMode('add');
    setSelectedContractor(null);

    const preselectedProperty = selectedFilters.property_id
      ? {
        property_id: selectedFilters.property_id,
        building_name: selectedFilters.building_name || '',
      }
      : null;

    formik.resetForm({
      values: {
        ...defaultContractor,
        properties: preselectedProperty ? [preselectedProperty] : [],
      },
    });

    setIsModalOpen(true);
  };

  const handleEditClick = (contractor) => {
    setFormMode('edit');
    setSelectedContractor(contractor);

    const initialValues = getContractorInitialValues('edit', contractor);
    formik.setValues(initialValues);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedContractor?._id) return;

    try {
      dispatch(setLoading(true));
      await deleteContractor(selectedContractor._id);
      Swal.fire('Deleted', 'Contractor deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedContractor(null);
      fetchContractors();
    } catch {
      Swal.fire('Error', 'Failed to delete contractor', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFilterChange = useCallback((updater) => {
    setSelectedFilters((prev) =>
      typeof updater === 'function' ? updater(prev) : updater
    );
  }, []);

  useEffect(() => {
    fetchProperties();
    fetchCategories();
  }, [fetchProperties, fetchCategories]);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Contractors / Service Providers</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Contractors" />

        <PropertySelector
          propertyList={properties}
          selectedFilters={selectedFilters}
          onPropertySelectionChange={handleFilterChange}
          onAddClick={handleAddClick}
          user={user}
          categories={categories}
        />

        <ContractorsListTable
          items={contractors}
          onEdit={handleEditClick}
          onDelete={(c) => {
            setSelectedContractor(c);
            setIsDeleteModalOpen(true);
          }}
          userRole={user?.role || 'other'}
          onRefresh={fetchContractors}
        />

        <AddEditContractorModal
          formik={formik}
          availableProperties={properties}
          filters={selectedFilters}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isEdit={formMode === 'edit'}
          submitContractor={submitContractor}
          onRefresh={fetchContractors}
          userRole={user?.role || 'other'}
          userId={user?.id || ''}
          categories={categories}
        />

        <DeleteContractorConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          contractor={selectedContractor}
        />
      </div>
    </div>
  );
};

export { Contractors };